import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Proximiio from "proximiio-js-library";
import { FloorModel } from "proximiio-js-library/lib/models/floor";
import { useIdle, useMedia } from "react-use";
import MapView from "@/components/MapView";
import FloorPicker from "@/components/FloorPicker";
import PoiSearch from "@/components/PoiSearch";
import Sidebar from "@/components/Sidebar";
import LocateMeButton from "@/components/LocateMeButton";
import LanguageToggle from "@/components/LanguageToggle";
import ResetViewButton from "@/components/ResetViewButton";
import { Toaster } from "@/components/ui/toaster";
import useMapStore from "@/store/mapStore";
import LoadingGifNoText from "@/assets/cc-loading.gif";
import LoadingBG from "@/assets/loading-bg.png";
import { cn } from "./lib/utils";
import DateTime from "./components/DateTime";
import FloorUpAnimation from "@/assets/floors-to-arrow-up.gif";
import FloorDownAnimation from "@/assets/floors-to-arrow-down.gif";
import FloorUpAnimationAR from "@/assets/floor-to-arrow-up-AR.gif";
import FloorDownAnimationAR from "@/assets/floor-to-arrow-down-AR.gif";
import Overlay from "./components/Overlay";

function App() {
  const showLanguageToggle =
    import.meta.env.VITE_WAYFINDING_SHOW_LANGUAGE_TOGGLE === "true";
  const showResetViewButton =
    import.meta.env.VITE_WAYFINDING_SHOW_RESET_BUTTON === "true";
  const showDatetime = import.meta.env.VITE_WAYFINDING_SHOW_DATETIME === "true";
  const animationsEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_LEVELCHANGE_ANIMATIONS === "true";
  const { i18n } = useTranslation();
  const sessionIsIdle = useIdle(
    import.meta.env.VITE_WAYFINDING_SESSION_TIMEOUT,
  );
  const wideScreen = useMedia("(min-width: 1024px)");

  const [appInitiated, setAppInitiated] = useMapStore((state) => [
    state.appInitiated,
    state.setAppInitiated,
  ]);
  const [kioskMode, setKioskMode] = useMapStore((state) => [
    state.kioskMode,
    state.setKioskMode,
  ]);

  const map = useMapStore((state) => state.map);
  const mapLoaded = useMapStore((state) => state.mapLoaded);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const activeFilter = useMapStore((state) => state.activeFilter);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const overlayContent = useMapStore((state) => state.overlayContent);
  const isLoading = useMapStore((state) => state.isLoading);
  const currentLang = useMapStore((state) => state.currentLang);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const stepAnimated = useMapStore((state) => state.stepAnimated);
  const currentStep = useMapStore((state) => state.currentStep);
  const prevFloorRef = useRef<FloorModel>(currentFloor);
  const prevHaveRouteDetailsRef = useRef<boolean>(haveRouteDetails);

  const setCurrentLang = useMapStore((state) => state.setCurrentLang);
  const setAds = useMapStore((state) => state.setAds);
  const setAppSession = useMapStore((state) => state.setAppSession);
  const resetViewStore = useMapStore((state) => state.resetView);
  const locateMe = useMapStore((state) => state.locateMe);
  const setAccessibleRoute = useMapStore((state) => state.setAccessibleRoute);
  const setMobileMode = useMapStore((state) => state.setMobileMode);

  const [animationInProgress, setAnimationInProgress] = useState(false);
  const [animationType, setAnimationType] = useState("");

  const idleTime = haveRouteDetails ? 50e3 : 30e3; // 50/30 seconds to idle time
  const kioskIsIdle = useIdle(idleTime);
  const mapReady = useRef(false);

  // handle reset to default view
  const resetView = useCallback(() => {
    console.log("RESET VIEW");
    resetViewStore();
    locateMe();
    setAccessibleRoute({ accessibleRoute: false, forceAccessibleRoute: false });
    setCurrentLang("en");
    if (Object.keys(map).length > 0) {
      map.refetch();
      map.setFeaturesHighlight([]);
    }
  }, [map, resetViewStore, locateMe, setAccessibleRoute, setCurrentLang]);

  // This effect hook handles URL query parameters related to language and kiosk mode
  useEffect(() => {
    if (appInitiated) return;

    // Extract URL query parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Retrieve 'language' parameter from URL or default to 'en' if not found
    const urlLanguage = urlParams.get("language") ?? "en";

    // Update language state with 'language' parameter from URL
    setCurrentLang(urlLanguage);
    i18n.changeLanguage(urlLanguage);

    const rootHtml = document.getElementById("root-html");
    if (rootHtml) {
      rootHtml.setAttribute("dir", urlLanguage === "ar" ? "rtl" : "ltr");
      rootHtml.setAttribute("data-dir", urlLanguage === "ar" ? "rtl" : "ltr");
    }

    document.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length > 1) {
          event.preventDefault();
        }
      },
      { passive: false },
    );

    document.addEventListener(
      "wheel",
      (event) => {
        const { ctrlKey } = event;
        if (ctrlKey) {
          event.preventDefault();
          return;
        }
        //event.preventDefault();
        //event.stopPropagation();
      },
      { passive: false },
    );

    // If 'kiosk' parameter exists in URL, set kiosk mode
    if (urlParams.get("kiosk")) {
      setKioskMode(true);
    }

    setAppInitiated(true);
  }, [appInitiated, i18n, setKioskMode, setCurrentLang, setAppInitiated]);

  // This effect hook handles kioskMode changes
  useEffect(() => {
    if (kioskMode && kioskIsIdle && !mobileMode) {
      console.log("KIOSK IS IDLE, RESEEEET");
      resetView();
    }
  }, [kioskMode, kioskIsIdle, mobileMode, resetView]);

  // This effect hook handles ads initiation
  useEffect(() => {
    if (Object.keys(map).length > 0) {
      Proximiio.Ads.getAds().then((res) => {
        setAds(res.data);
      });
      setTimeout(() => {
        mapReady.current = true;
      }, 500);
    }
  }, [map, setAds]);

  // This effect hook set app session on init
  useEffect(() => {
    setAppSession();
  }, [setAppSession]);

  // This effect hook set reset app session on idle time
  useEffect(() => {
    if (sessionIsIdle) {
      setAppSession();
    }
  }, [sessionIsIdle, setAppSession]);

  // This effect hook handle level change animations
  const handleAnimationType = useCallback(
    (currentLevel: number, prevLevel: number) => {
      let animation = "";
      if (currentLevel > prevLevel) {
        animation =
          currentLang === "ar" ? FloorUpAnimationAR : FloorUpAnimation;
      } else if (currentLevel < prevLevel) {
        animation =
          currentLang === "ar" ? FloorDownAnimationAR : FloorDownAnimation;
      }
      setAnimationType(animation);
    },
    [currentLang],
  );
  useEffect(() => {
    if (
      haveRouteDetails &&
      prevHaveRouteDetailsRef.current &&
      Object.keys(currentFloor).length > 0 &&
      Object.keys(prevFloorRef.current).length &&
      currentFloor.id !== prevFloorRef.current.id
    ) {
      console.log("Animation", stepAnimated);
      handleAnimationType(currentFloor.level, prevFloorRef.current.level);
      if (stepAnimated.length > 0 && !stepAnimated.includes(currentStep)) {
        setAnimationInProgress(true);
      }
      map.stopRouteAnimation();
      console.log("Animation started");
      setTimeout(() => {
        console.log("Animation finished");
        setAnimationInProgress(false);
        if (
          Object.keys(map).length > 0 &&
          haveRouteDetails &&
          !stepAnimated.includes(currentStep)
        ) {
          map.restartRouteAnimation({ delay: 1000, recenter: false });
        }
      }, 2000);
    }
    prevFloorRef.current = currentFloor;
    prevHaveRouteDetailsRef.current = haveRouteDetails;
  }, [
    haveRouteDetails,
    currentFloor,
    handleAnimationType,
    map,
    stepAnimated,
    currentStep,
  ]);

  useEffect(() => {
    setMobileMode(!wideScreen);
  }, [wideScreen, setMobileMode]);

  return (
    <>
      {appInitiated && (
        <main>
          <div className="flex">
            <div className={cn("relative flex w-full")}>
              {!mapLoaded && (
                <div
                  className={cn(
                    "absolute flex h-screen w-full bg-[#7A7A7A]",
                    !mapReady.current && "z-[10]",
                  )}
                >
                  <div className="overlay absolute z-[10] h-full w-full bg-black/70"></div>
                  <img
                    src={LoadingBG}
                    alt="Mall of Emirates"
                    className={cn("object-cover", !mapReady.current && "z-[9]")}
                  />
                  <div className="absolute left-0 right-0 z-[11] flex h-full items-center justify-center lg:left-[380px] lg:right-0">
                    <img
                      src={LoadingGifNoText}
                      alt="Mall of Emirates"
                      className="w-32 lg:w-96"
                    />
                  </div>
                </div>
              )}
              {isLoading && mapLoaded && !animationInProgress && (
                <div className="absolute flex h-screen w-full">
                  <div className="overlay absolute left-0 z-[10] h-full w-full bg-black/70 lg:z-10"></div>
                  <div className="absolute bottom-20 left-0 right-0 z-[11] flex h-full items-center justify-center lg:bottom-0 lg:left-[380px] lg:right-0">
                    <img
                      src={LoadingGifNoText}
                      alt="Mall of Emirates"
                      className="w-32 lg:w-96"
                    />
                  </div>
                </div>
              )}
              {animationsEnabled && animationInProgress && (
                <div className="absolute z-10 flex h-screen w-full bg-black/70">
                  <div
                    className={cn(
                      "flex h-full w-[90%] flex-col items-center justify-center lg:group-data-[dir=ltr]:ml-96 lg:group-data-[dir=rtl]:mr-96",
                      activeFilter?.id && mobileMode && "h-[48%]",
                      routeFinish?.id && mobileMode && "h-[76%]",
                    )}
                  >
                    <img
                      src={animationType}
                      className="w-full group-data-[dir=rtl]:mr-24 lg:mx-0 lg:w-3/4"
                    />
                  </div>
                </div>
              )}
              {overlayContent && <Overlay content={overlayContent} />}
              {!routeFinish?.id && <PoiSearch />}
              <Sidebar />
              <FloorPicker />
              <LocateMeButton />
              {showLanguageToggle && !mobileMode && <LanguageToggle />}
              {showDatetime && <DateTime />}
              {showResetViewButton && <ResetViewButton />}
              <MapView />
              <Toaster />
            </div>
          </div>
        </main>
      )}
    </>
  );
}

export default App;
